import { isEmpty } from 'lodash';
import { getLocation } from '@sda/utils/location';
import { buildURIFromParams } from '../url';
import { getDateTime } from '../date';
import type { UnknownLocation } from '../location/interfaces';
import type {
  AlgoliaArrayFilter, AlgoliaBooleanFilter, AlgoliaBooleanGroupFilter, AlgoliaFilter,
  AlgoliaStringFilter, AlgoliaTypeFilter, QueryState,
} from './interfaces';
import type { AlgoliaIndices } from '@sda/enums/algolia';
import type { SearchOptions } from '@algolia/client-search';

interface ConnectSearchForm {
  readonly query: string;
  readonly latLng: string;
  readonly radius: number;
  readonly searchString: string;
  readonly capacity: string;
  readonly hasPublicImageUrl: string;
  readonly hasFinishedShow: boolean;
  readonly onlyShowVetted: string;
  readonly isPresenter: boolean;
  readonly actType: string;
}

/**
 * Prepends the current environment to the Algolia index to create a formatted algolia index name
 * @param  {AlgoliaIndices} sourceIndexName
 * @return
 */
export const getAlgoliaIndexName = (sourceIndexName: AlgoliaIndices): string => {
  // Always use production, staging, or development indices
  const algoliaEnv = process.env.NODE_ENV as 'production' | 'staging' | 'development';

  return `${algoliaEnv}_${sourceIndexName}`;
};

/**
 * Takes object containing lat & lng, and returns a string formatted for the algolia _geoloc field
 * @param lat: Latitude
 * @param lng: Longitude
 */
export const buildGeolocString = (lat: number, lng: number): string => `${lat}, ${lng}`;

type LocationObject = Pick<SearchOptions, 'aroundRadius' | 'aroundLatLng' | 'aroundLatLngViaIP'>;

/**
 * Returns a location object based on given search parameters
 */
export const getLocationObject = (searchParams: Partial<Pick<ConnectSearchForm, 'radius' | 'latLng'>>): LocationObject => {
  const radius = searchParams.radius || null;

  const latLng = searchParams.latLng || null;
  const location = latLng ? getLocation(latLng) : null;

  let locationObject: LocationObject = radius ? { aroundRadius: radius } : {};

  if (location) {
    locationObject = {
      ...locationObject,
      aroundLatLng: buildGeolocString(location.lat, location.lng),
    };
  } else {
    locationObject = {
      ...locationObject,
      aroundLatLngViaIP: true,
    };
  }

  return locationObject;
};

export const getLocationString = (latLng: UnknownLocation) => {
  if (!latLng || isEmpty(latLng)) {
    return '';
  }

  const location = getLocation(latLng);

  return `${location.lat}, ${location.lng}`;
};

/**
 * Constructs a URL based on the current query state
 * @param state
 * @returns
 */
export const createURL = (state: QueryState): string => {
  let uriParams: QueryState = {};
  let url = '';

  if (state.query) {
    uriParams.query = state.query;
  }

  if (state.page) {
    uriParams.page = state.page;
  }

  url = buildURIFromParams(url, uriParams);

  return url;
};

/**
 * Builds boolean filter
 * broken out to standalone function because it's used in two different places
 * @param attribute
 * @param filter
 * @return {string}
 */
const serializeBooleanFilter = (attribute: string, filter: AlgoliaBooleanFilter) => `${filter.not ? 'NOT ' : ''}${attribute}:${filter.value}`;

/**
 * Builds string filter
 * @param attribute
 * @param filter
 * @return {string}
 */
const serializeStringFilter = (attribute: string, filter: AlgoliaStringFilter) => `${filter.not ? 'NOT ' : ''}${attribute}:${filter.value}`;

/**
  * Builds string filter from array
  * @param attribute
  * @param filter
  * @return {string}
  */
const serializeArrayFilter = (attribute: string, filter: AlgoliaArrayFilter) => {
  let filterString = '';

  const andOr = filter.or ? 'OR' : 'AND';

  filter.value.forEach((value) => {
    filterString += `${filterString.length > 0 ? ` ${andOr} ` : '('}${filter.not ? 'NOT ' : ''}${attribute}:${value}`;
  });

  return `${filterString})`;
};

/**
 * Serializes a filter entity
 * @param attribute
 * @param filter
 * @return {string}
 */
const serializeFilterEntity = (attribute: string, filter: AlgoliaTypeFilter): string => {
  switch (filter.type) {
    case 'boolean':
      return serializeBooleanFilter(attribute, filter);
    case 'string':
      return serializeStringFilter(attribute, filter);
    case 'tag':
      return `${filter.not ? 'NOT ' : ''}${attribute}`;
    case 'after':
      return `${filter.not ? 'NOT ' : ''}${attribute} >${filter.inclusive ? '=' : ''} ${filter.value.toSeconds()}`;
    case 'before':
      return `${filter.not ? 'NOT ' : ''}${attribute} <${filter.inclusive ? '=' : ''} ${filter.value.toSeconds()}`;
    case 'futureDate':
      return `${filter.not ? 'NOT ' : ''}${attribute} > ${getDateTime().toSeconds()}`;
    case 'pastDate':
      return `${filter.not ? 'NOT ' : ''}${attribute} < ${getDateTime().toSeconds()}`;
    case 'array':
      return serializeArrayFilter(attribute, filter);
    default:
      return '';
  }
};

/**
  * Builds an OR sub-query
  * @param filter
  * @return {string}
  */
const serializeFilterGroupSubString = (filter: AlgoliaBooleanGroupFilter): string => {
  let filterString = '';

  Object.entries(filter.group).forEach(([attribute, payload]) => {
    filterString += `${filterString.length > 0 ? ' OR ' : '('}${serializeFilterEntity(attribute, payload)}`;
  });

  return `${filterString})`;
};

/**
 * Serializes an object of filter entities
 * Currently filters are all assumed to be separated by AND, however OR groups are supported by
 * @param filters
 * @return {string}
 */
export const serializeFilters = (filters: Record<string, AlgoliaFilter>): string => {
  if (!filters) {
    return '';
  }

  let filterString = '';

  Object.entries(filters).forEach(([attribute, filter]) => {
    filterString += `${filterString.length > 0 ? ' AND ' : ''}${filter.type === 'filterGroup' ? serializeFilterGroupSubString(filter) : serializeFilterEntity(attribute, filter)}`;
  });

  return filterString;
};

export const urlToSearchState = () => {
  const url = new URL(window?.location?.href);
  const params = url.searchParams;

  let searchState: QueryState = {
    query: decodeURIComponent(params.get('query') || ''),
    page: parseInt(`${params.get('page')}`, 10) || 1,
  };

  return searchState;
};
